const DEFAULT_LOCALE = 'en-US';

const pad = (value: number) => value.toString().padStart(2, '0');

const normalizeToDate = (value?: string | number | Date): Date | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const dateValue =
    typeof value === 'number'
      ? new Date(value)
      : typeof value === 'string'
        ? new Date(value)
        : value;

  return Number.isNaN(dateValue.getTime()) ? null : dateValue;
};

const DEFAULT_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  hourCycle: 'h23',
};

export const formatTimestampToLocaleString = (
  value?: string | number | Date,
  locale: string = DEFAULT_LOCALE,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const normalizedDate = normalizeToDate(value);
  if (!normalizedDate) return '';

  return normalizedDate.toLocaleString(locale, {
    ...DEFAULT_FORMAT_OPTIONS,
    ...(options ?? {}),
  });
};

export const formatDateTimeForKstInput = (value?: Date | string | null): string => {
  const normalizedDate = normalizeToDate(value ?? undefined);
  if (!normalizedDate) return '';

  return `${normalizedDate.getFullYear()}-${pad(normalizedDate.getMonth() + 1)}-${pad(
    normalizedDate.getDate(),
  )}T${pad(normalizedDate.getHours())}:${pad(normalizedDate.getMinutes())}`;
};

export const formatDateTimeForUtcInput = (value?: Date | string | null): string => {
  const normalizedDate = normalizeToDate(value ?? undefined);
  if (!normalizedDate) return '';

  return `${normalizedDate.getUTCFullYear()}-${pad(normalizedDate.getUTCMonth() + 1)}-${pad(
    normalizedDate.getUTCDate(),
  )}T${pad(normalizedDate.getUTCHours())}:${pad(normalizedDate.getUTCMinutes())}`;
};

const parseLocalDateTimeParts = (localInput: string): {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
 } | null => {
  if (!localInput.trim()) {
    return null;
  }

  const [datePart, timePart] = localInput.split('T');
  if (!datePart || !timePart) {
    return null;
  }

  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return null;
  }

  return { year, month, day, hours, minutes };
};

export const parseLocalDateTimeToUtcDate = (localInput: string): Date | null => {
  const parts = parseLocalDateTimeParts(localInput);
  if (!parts) return null;

  const date = new Date(parts.year, parts.month - 1, parts.day, parts.hours, parts.minutes);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const parseLocalDateTimeToUtcTimestamp = (localInput: string): number | null => {
  const date = parseLocalDateTimeToUtcDate(localInput);
  return date ? date.getTime() : null;
};

export const parseLocalDateTimeToUtcISOString = (localInput: string): string | null => {
  const date = parseLocalDateTimeToUtcDate(localInput);
  return date ? date.toISOString() : null;
};
